import { Stack, Typography, Card, Box, useTheme, useMediaQuery } from '@mui/material';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import { PaginationArrows } from '../../components/Pagination/PaginationArrows';
import { SnapItem, SnapList } from 'react-snaplist-carousel';
import { useCarousel } from '@shared/hooks';
import { LatestArticleItem } from './LatestArticleItem';

interface Props {
  title: string;
  articles: LatestArticleItem[];
}

export function LatestArticles({ title, articles }: Props) {
  const { snapListRef, firstItemIsVisible, lastItemIsVisible, scrollForward, scrollBack } =
    useCarousel(articles.length);
  const { breakpoints } = useTheme();
  const querySmall = useMediaQuery(breakpoints.up('sm'));

  function renderTitleDesktop() {
    if (!querySmall) return null;

    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        paddingInline={5}
        sx={{
          background: ({ palette }) => palette.primary[800],
          [breakpoints.down('sm')]: {
            display: 'none',
          },
        }}
      >
        <Typography variant="h6" component="h2" color="common.white">
          {title}
        </Typography>
      </Stack>
    );
  }

  function renderTitleMobile() {
    if (querySmall) return null;

    return (
      <SnapItem snapAlign="start">
        <Stack
          justifyContent="center"
          alignItems="center"
          paddingInline={2}
          aria-label={title}
          sx={{
            background: ({ palette }) => palette.primary[800],
            color: 'common.white',
          }}
        >
          <SettingsInputAntennaIcon aria-hidden />
        </Stack>
      </SnapItem>
    );
  }

  return (
    <Card
      sx={{
        padding: 0,
        position: 'relative',
        [breakpoints.down('md')]: {
          padding: 0,
        },
      }}
    >
      <Stack direction="row" alignItems="stretch">
        {renderTitleDesktop()}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            containerType: 'inline-size',
          }}
        >
          <SnapList ref={snapListRef} direction="horizontal">
            {renderTitleMobile()}
            {articles.map((article, index) => (
              <SnapItem
                snapAlign={querySmall ? 'center' : 'start'}
                key={index}
                margin={{
                  left: index === 0 ? '0px' : '1px',
                  right: index === articles.length - 1 ? '100px' : '0px',
                }}
              >
                <LatestArticleItem key={article.url} {...article} />
              </SnapItem>
            ))}
          </SnapList>
        </Box>
      </Stack>
      <Stack
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          paddingRight: 1,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          width: '104px',
          background: 'linear-gradient(90deg, transparent 0%, #fff2 25%, #fffb 60%, #fffe 100%)',
        }}
      >
        <PaginationArrows
          disableNext={lastItemIsVisible}
          disablePrevious={firstItemIsVisible}
          handleNext={scrollForward}
          handlePrevious={scrollBack}
          hideWhenDisabled
        />
      </Stack>
    </Card>
  );
}
